<template>
    <div>
        <div v-if="!fetchingInstanceInvitations" class="d-flex flex-wrap align-center">
            <InstanceInviteDialog :instanceName="currentInstanceName || ''" />
            <v-spacer />
            <v-text-field
                v-model="search"
                :disabled="!instanceInvitations.length"
                placeholder="Search for an invitation..."
                prepend-inner-icon="mdi-magnify"
                solo
                flat
                dense
                hide-details
                clearable>
                <template v-slot:append>
                    <v-menu offset-y :close-on-content-click="false">
                        <template v-slot:activator="{ on, attrs }">
                            <v-btn v-bind="attrs" v-on="on" small class="mr-n2" :disabled="!instanceInvitations.length">
                                <v-icon left>mdi-tune-variant</v-icon>
                                <span>Filters</span>
                            </v-btn>
                        </template>
                        <v-list nav dense class="d-flex align-start">
                            <div class="filter-section">
                                <v-subheader>STATUS</v-subheader>
                                <div v-for="invitationType in availableInvitationTypes" :key="invitationType">
                                    <v-sheet rounded class="py-2 px-4 my-1">
                                        <v-switch v-model="invitationsSwitchData[invitationType]" inset dense hide-details class="pa-0 ma-0">
                                            <template v-slot:label>
                                                <div class="d-flex align-center">
                                                    <v-icon left small :color="getMailIconColor(invitationType)">mdi-email-outline</v-icon>
                                                    <span>{{ capitalize(invitationAndRequestStatusTypes[invitationType]) }}</span>
                                                </div>
                                            </template>
                                        </v-switch>
                                    </v-sheet>
                                </div>
                            </div>
                            <v-divider vertical class="mx-2 align-self-stretch" />
                            <div class="filter-section">
                                <v-subheader>ROLE</v-subheader>
                                <div v-for="role in availableRoles" :key="role">
                                    <v-sheet rounded class="py-2 px-4 my-1">
                                        <v-switch v-model="roleFilters[role]" inset dense hide-details class="pa-0 ma-0">
                                            <template v-slot:label>
                                                <div class="d-flex align-center">
                                                    <v-icon left small>{{ instanceRole(role).icon }}</v-icon>
                                                    <span>{{ capitalize(instanceRole(role).label) }}</span>
                                                </div>
                                            </template>
                                        </v-switch>
                                    </v-sheet>
                                </div>
                            </div>
                        </v-list>
                    </v-menu>
                </template>
            </v-text-field>
            <v-tooltip bottom>
                <template v-slot:activator="{ on }">
                    <v-btn v-on="on" icon class="ml-2" @click="fetchInvitations"><v-icon>mdi-refresh</v-icon></v-btn>
                </template>
                <span>Refresh</span>
            </v-tooltip>
        </div>

        <v-data-table
            v-if="!fetchingInstanceInvitations"
            :headers="headers"
            :items="filteredInvitations"
            :items-per-page.sync="itemsPerPage"
            :footer-props="{ 'items-per-page-options': itemsPerPageOptions }"
            item-key="invid"
            class="mt-4">
            <template v-slot:[`item.invitation`]="{ item }">
                <div>{{ item.requestee_email || '-' }}</div>
            </template>
            <template v-slot:[`item.sent_on`]="{ item }">
                <div class="py-2 text-no-wrap">
                    <span v-if="item.notification_timestamp">{{ item.notification_timestamp | dateTimeToHuman }}</span>
                    <span v-else>-</span>
                </div>
            </template>
            <template v-slot:[`item.accepted_on`]="{ item }">
                <div class="py-2 text-no-wrap">
                    <span v-if="getInvitationResponseTimestamp(item, invitationAndRequestStatusTypes.ACCEPTED)">
                        {{ getInvitationResponseTimestamp(item, invitationAndRequestStatusTypes.ACCEPTED) | dateTimeToHuman }}
                    </span>
                    <span v-else>-</span>
                </div>
            </template>
            <template v-slot:[`item.rejected_on`]="{ item }">
                <div class="py-2 text-no-wrap">
                    <span v-if="getInvitationResponseTimestamp(item, invitationAndRequestStatusTypes.REJECTED)">
                        {{ getInvitationResponseTimestamp(item, invitationAndRequestStatusTypes.REJECTED) | dateTimeToHuman }}
                    </span>
                    <span v-else>-</span>
                </div>
            </template>
            <template v-slot:[`item.revoked_on`]="{ item }">
                <div class="py-2 text-no-wrap">
                    <span v-if="item.revocation_timestamp">{{ item.revocation_timestamp | dateTimeToHuman }}</span>
                    <span v-else>-</span>
                </div>
            </template>
            <template v-slot:[`item.status`]="{ item }">
                <v-chip outlined label small>
                    <v-icon left small :color="getMailIconColor(getInvitationStatus(item))">mdi-email-outline</v-icon>
                    {{ capitalize(getInvitationStatus(item)) }}
                </v-chip>
            </template>
            <template v-slot:[`item.roles`]="{ item }">
                <v-chip v-for="(role, index) in instanceRoles(item)" :key="`${item.invid}-${index}`" outlined label small>
                    <v-icon left small>{{ instanceRole(role.Name).icon }}</v-icon>
                    {{ capitalize(instanceRole(role.Name).label) }}
                </v-chip>
            </template>
            <template v-slot:[`item.actions`]="{ item }">
                <v-menu v-if="isActionable(item)" left offset-x nudge-top="10">
                    <template v-slot:activator="{ on }">
                        <v-btn v-on="on" text color="secondary" small><v-icon>more_horiz</v-icon></v-btn>
                    </template>
                    <v-list nav dense>
                        <InvitationActionsDialog
                            activatorType="listItem"
                            :successFetchAction="fetchInvitations"
                            :invitationInfo="item"
                            :actionType="actionTypes.RESEND_INVITATION" />
                        <CopyToClipboard :textToCopy="item.link" buttonName="Copy invitation link" type="listItem" />
                        <v-divider class="my-2" />
                        <InvitationActionsDialog
                            activatorType="listItem"
                            :successFetchAction="fetchInvitations"
                            :invitationInfo="item"
                            :actionType="actionTypes.REVOKE_INVITATION" />
                    </v-list>
                </v-menu>
            </template>
            <template v-slot:no-data>
                <span v-if="search">No invitations found.</span>
                <span v-else>No invitation history exists for this instance.</span>
            </template>
        </v-data-table>
        <v-skeleton-loader v-else type="table" />
    </div>
</template>

<script>
import { mapGetters, mapState } from 'vuex'
import { capitalize } from 'lodash'
import { instanceRole, sortArray } from '@/utils'
import { invitationsAndRequests } from '@/mixins/invitationsAndRequests'
import InstanceInviteDialog from '@/modules/instance/components/InstanceInviteDialog.vue'
const CopyToClipboard = () => import('@/components/CopyToClipboard.vue')
const InvitationActionsDialog = () => import('@/modules/invitations/components/InvitationsActionsDialog.vue')

const createDefaultInvitationsSwitchData = () => ({
    ACCEPTED: true,
    PENDING: true,
    REJECTED: true,
    REVOKED: true,
    EXPIRED: true,
    BROKEN: true,
    CREATED: true
})

const createDefaultRoleFilters = () => ({
    EDITOR: true,
    VIEWER: true,
    OBSERVER: true
})

export default {
    name: 'InstanceInvitations',
    components: { CopyToClipboard, InstanceInviteDialog, InvitationActionsDialog },
    mixins: [invitationsAndRequests],
    data() {
        return {
            search: '',
            itemsPerPage: 10,
            itemsPerPageOptions: [10, 25, 50, -1],
            invitationsSwitchData: createDefaultInvitationsSwitchData(),
            roleFilters: createDefaultRoleFilters(),
            headers: [
                { text: 'Invitation', value: 'invitation', sortable: false },
                { text: 'Status', value: 'status', sortable: false, width: '140px' },
                { text: 'Role', value: 'roles', sortable: false },
                { text: 'Sent on', value: 'sent_on', sortable: false, width: '180px' },
                { text: 'Accepted on', value: 'accepted_on', sortable: false, width: '180px' },
                { text: 'Rejected on', value: 'rejected_on', sortable: false, width: '180px' },
                { text: 'Revoked on', value: 'revoked_on', sortable: false, width: '180px' },
                { text: 'Actions', value: 'actions', align: 'right', sortable: false, width: '100px' }
            ]
        }
    },
    computed: {
        ...mapState('instanceStore', ['instanceInvitations', 'fetchingInstanceInvitations']),
        ...mapGetters('instanceStore', ['currentInstanceName']),
        availableInvitationTypes() {
            const invitationTypes = []
            this.instanceInvitations.forEach(invitation => {
                const invitationType = this.getInvitationStatus(invitation)
                if (!invitationTypes.includes(invitationType)) {
                    invitationTypes.push(invitationType)
                }
            })
            return invitationTypes
        },
        displayedInvitationTypes() {
            return Object.values(this.invitationAndRequestStatusTypes).filter(
                value => this.availableInvitationTypes.includes(value) && this.invitationsSwitchData[value]
            )
        },
        availableRoles() {
            return [...new Set(this.instanceInvitations.flatMap(invitation => this.instanceRoles(invitation).map(role => role.Name)))].filter(Boolean)
        },
        filteredInvitations() {
            const term = this.search?.trim().toLowerCase()
            const invitations = this.instanceInvitations.filter(invitation => {
                const matchesStatus = this.displayedInvitationTypes.includes(this.getInvitationStatus(invitation))
                const matchesRole = this.instanceRoles(invitation).some(role => this.roleFilters[role.Name])
                const matchesSearch = !term || invitation.requestee_email?.toLowerCase().includes(term)
                return matchesStatus && matchesRole && matchesSearch
            })
            return sortArray(invitations, 'invid', 'descending', false)
        }
    },
    methods: {
        capitalize,
        instanceRole,
        getInvitationResponseTimestamp(invitation, responseType) {
            const responses = invitation?.invitation_responses || []
            return responses.find(response => response.response === responseType)?.timestamp || null
        },
        fetchInvitations() {
            this.invitationsSwitchData = createDefaultInvitationsSwitchData()
            this.roleFilters = createDefaultRoleFilters()
            return this.$store.dispatch('instanceStore/fetchInstanceInvitations', this.$route.params.iid)
        },
        instanceRoles(invitation) {
            return (invitation.roles || []).filter(
                role => role.role_type === this.invitationAndRequestRoleTypes.INSTANCE_ROLE && role.object.toString() === this.$route.params.iid.toString()
            )
        },
        isActionable(invitation) {
            const status = this.getInvitationStatus(invitation)
            return status === this.invitationAndRequestStatusTypes.PENDING || status === this.invitationAndRequestStatusTypes.CREATED
        }
    }
}
</script>

<style scoped>
.filter-section {
    min-width: 190px;
}
</style>
